package game.entity

import game.Game
import game.ROOM_HEIGHT
import game.ROOM_WIDTH
import game.Room
import kotlin.random.Random

class PongBattleConfig(
    val leftPaddleSpriteId: String,
    val rightPaddleSpriteId: String,
    val puckSpriteId: String,
    val showScore: Boolean,
    val puckSpeed: Double,
    val opponentSpeed: Int,
    val upperBound: Double,
    var lowerBound: Double,
)

enum class PongPuckState {
    Serve,
    InPlay,
    Score
}

class PongPuck(val pong: PongBattle) : Entity() {
    val serveCoolDown = 50
    var coolDown = serveCoolDown
    var state = PongPuckState.Serve
    var trueX = 25.0
    var trueY = 10.0
    var velX = 0.0
    var velY = 0.0
    var speed = pong.config.puckSpeed
    val vspeed = 1.9

    var onPlayerScore: (Game) -> Unit = {}
    var onOpponentScore: (Game) -> Unit = {}

    override fun onInit(game: Game, room: Room) {
        sprite = game.getSpriteById(pong.config.puckSpriteId)
        chooseServeDirection()
    }

    override fun onTick(game: Game, room: Room) {
        when (state) {
            PongPuckState.Serve -> {
                if (coolDown > 0) {
                    coolDown--
                } else {
                    state = PongPuckState.InPlay
                }
            }
            PongPuckState.InPlay -> {
                if (velX > 0) {
                    velX += 0.001
                } else {
                    velX -= 0.001
                }

                trueX += velX
                trueY += velY

                if (trueY !in pong.config.upperBound..pong.config.lowerBound) {
                    velY = -velY
                    game.playSound("ping")
                }
                if (trueX in 3.0..4.0) {
                    if (checkRebound(pong.leftPaddle)) {
                        game.playSound("pong")
                    }
                }
                if (trueX in ROOM_WIDTH - 3.0..ROOM_WIDTH - 2.0) {
                    if (checkRebound(pong.rightPaddle)) {
                        game.playSound("pong")
                    }
                }
                if (trueX < 1) {
                    centerOnRoom()
                    onPlayerScore.invoke(game)
                    state = PongPuckState.Score
                } else if (trueX > ROOM_WIDTH - 2) {
                    centerOnRoom()
                    onOpponentScore.invoke(game)
                    state = PongPuckState.Score
                }
            }
            PongPuckState.Score -> {
                chooseServeDirection()
                state = PongPuckState.Serve
                coolDown = serveCoolDown
            }
        }
        col = trueX.toInt()
        row = trueY.toInt()
    }

    private fun checkRebound(paddle: Entity): Boolean {
        return if (trueY in paddle.row + 0.0..paddle.row + 3.0) {
            velY = when (trueY) {
                in paddle.row + 0.0..paddle.row + 1.0 -> -(speed / vspeed)
                in paddle.row + 1.0..paddle.row + 1.5 -> -(speed / vspeed / 2)
                in paddle.row + 1.5..paddle.row + 2.0 -> (speed / vspeed / 2)
                in paddle.row + 2.0..paddle.row + 3.0 -> (speed / vspeed)
                else -> 0.0
            }
            velX = -velX
            trueX += velX // Move out of paddle range
            true
        } else {
            false
        }
    }

    private fun chooseServeDirection() {
        trueY = Random.nextDouble(2.0, ROOM_HEIGHT - 3.0)
        velX = speed

        velY = when (Random.nextBoolean()) {
            true -> -(speed / vspeed)
            false -> (speed / vspeed)
        }
    }

    private fun centerOnRoom() {
        trueX = ROOM_WIDTH / 2.0
        trueY = ROOM_HEIGHT / 2.0
    }
}

class PlayerPaddle(val pong: PongBattle) : Entity() {
    override fun onInit(game: Game, room: Room) {
        sprite = game.getSpriteById(pong.config.leftPaddleSpriteId)
        col = 48
        row = 9
    }

    override fun onTick(game: Game, room: Room) {
        row = game.player.row - 1
    }
}

class OpponentPaddle(val pong: PongBattle) : Enemy() {
    var coolDown = 0
    val speed = pong.config.opponentSpeed
    override val deathSound: String? = "enemy-explode"

    override fun onInit(game: Game, room: Room) {
        sprite = game.getSpriteById(pong.config.rightPaddleSpriteId)
        col = 2
        row = 10
        width = 2
        height = 3
    }

    override fun onTick(game: Game, room: Room) {
        if (coolDown > 0) {
            coolDown--
        } else {
            if (pong.puck.row < row && row > 0) {
                row--
                coolDown = speed
            } else if (pong.puck.row > row + 2 && row < ROOM_HEIGHT - 2) {
                row++
                coolDown = speed
            }
        }
    }
}

class PongBattle(val config: PongBattleConfig, var onDone: () -> Unit) : Entity() {
    lateinit var game: Game

    var playerScore = Score().apply {
        col = 31
    }
    var opponentScore = Score().apply {
        col = 19
    }

    val rightPaddle = PlayerPaddle(this)
    val leftPaddle = OpponentPaddle(this)
    val puck = PongPuck(this)
    val wall1 = InvisibleWall(48, 0).also {
        it.height = ROOM_HEIGHT
    }
    val wall2 = InvisibleWall(50, 0).also {
        it.height = ROOM_HEIGHT
    }
    val wall3 = InvisibleWall(49, 0)
    val wall4 = InvisibleWall(49, ROOM_HEIGHT - 1)

    override fun onInit(game: Game, room: Room) {
        this.game = game

        room.addEntity(leftPaddle)
        room.addEntity(rightPaddle)
        room.addEntity(puck)
        if (config.showScore) {
            room.addEntity(playerScore)
            room.addEntity(opponentScore)
        }
        room.addEntity(wall1)
        room.addEntity(wall2)
        room.addEntity(wall3)
        room.addEntity(wall4)

        puck.onPlayerScore = {
            playerScore.value++

            if (playerScore.value >= 7) {
                onDone.invoke()
            } else {
                leftPaddle.takeDamage(game, 0)
            }
        }

        puck.onOpponentScore = {
            opponentScore.value++
            it.player.takeDamage(1)
        }
    }

    override fun onTick(game: Game, room: Room) {
    }

    override fun onRemove(game: Game, room: Room) {
        leftPaddle.killMe(game)
//        room.removeEntity(leftPaddle)
        room.removeEntity(rightPaddle)
        room.removeEntity(puck)
        room.removeEntity(playerScore)
        room.removeEntity(opponentScore)
        room.removeEntity(wall1)
        room.removeEntity(wall2)
        room.removeEntity(wall3)
        room.removeEntity(wall4)
    }
}